import { trackById } from 'src/app/shared/utils/track-by.util';
import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs/operators";
import {
    Chart,
    DoughnutController,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";
import { IDashboardWidget } from "src/app/models/dashboard";
import { CalibrationEquipmentService } from "src/app/services/calibration-equipment.service";
import { CommonService } from "src/app/services/common.service";
import { WidgetOrgFilterComponent } from "src/app/common-components/widget-org-filter/widget-org-filter.component";
import {
    CalibrationStatusSummaryResponse,
    CalibrationStatusItem,
} from "src/app/models/calibration-status-summary.model";
import { RoutePaths } from "src/app/shared/constants/routePaths.constants";
import { BaseDashboardWidget } from "src/app/components/dashboard/dashboard-widgets/_shared/base-dashboard-widget";

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const CHART_TYPE = "doughnut" as const;
type ChartType = typeof CHART_TYPE;

@Component({
    standalone: true,
    selector: "app-equipment-calibration-status-widget",
    imports: [CommonModule, TranslateModule, WidgetOrgFilterComponent],
    templateUrl: "./equipment-calibration-status-widget.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./equipment-calibration-status-widget.component.scss"]
})
export class EquipmentCalibrationStatusWidgetComponent
    extends BaseDashboardWidget
    implements OnInit, OnDestroy, OnChanges, AfterViewInit, IDashboardWidget
{
    @ViewChild("chartCanvas") chartCanvas!: ElementRef<HTMLCanvasElement>;

    summary?: CalibrationStatusSummaryResponse;
    private chart?: Chart<ChartType>;
    private dataLoaded = false;
    private viewInitialized = false;
    private chartRenderTimeout?: number;

    readonly colors = {
        overdue: "#ef4444",
        upcoming: "#f59e0b",
        calibrated: "#22c55e",
    };

    constructor(
        private calibrationEquipmentService: CalibrationEquipmentService,
        private commonService: CommonService,
        private translate: TranslateService,
        private router: Router,
    ) {
        super();
    }

    ngAfterViewInit(): void {
        this.viewInitialized = true;
        if (this.dataLoaded) {
            this.createChart();
        }
    }

    protected override reload(): void {
        this.loadData();
    }

    protected override onDestroy(): void {
        if (this.chartRenderTimeout) {
            clearTimeout(this.chartRenderTimeout);
        }
        this.chart?.destroy();
    }

    navigateToDetail(item: CalibrationStatusItem): void {
        this.router.navigate([RoutePaths.calibrationDetails, item.id]);
    }

    private loadData(): void {
        this.isLoading = true;
        this.error = undefined;

        const effectiveOrgId = this.resolveEffectiveOrgIdAndRecord();
        this.calibrationEquipmentService
            .getCalibrationStatusSummary(effectiveOrgId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.summary = response;
                    this.dataLoaded = true;
                    this.isLoading = false;

                    if (this.chartRenderTimeout) {
                        clearTimeout(this.chartRenderTimeout);
                    }
                    this.chartRenderTimeout = window.setTimeout(() => {
                        if (this.viewInitialized) {
                            this.createChart();
                        }
                    });
                },
                error: () => {
                    this.error = this.translate.instant(
                        "Failed to load calibration status data",
                    );
                    this.summary = undefined;
                    this.dataLoaded = false;
                    this.chart?.destroy();
                    this.chart = undefined;
                    this.isLoading = false;
                },
            });
    }

    private createChart(): void {
        if (!this.chartCanvas?.nativeElement || !this.summary) return;

        this.chart?.destroy();

        const data = [
            this.summary.overdueCalibration,
            this.summary.upcomingCalibration,
            this.summary.calibrated,
        ];

        // If all zeros, show a grey placeholder ring
        const hasData = data.some((v) => v > 0);

        this.chart = new Chart(this.chartCanvas.nativeElement, {
            type: CHART_TYPE,
            data: {
                labels: [
                    this.translate.instant("Overdue Calibration"),
                    this.translate.instant("Upcoming Calibration"),
                    this.translate.instant("Calibrated"),
                ],
                datasets: [
                    {
                        data: hasData ? data : [1],
                        backgroundColor: hasData
                            ? [
                                  this.colors.overdue,
                                  this.colors.upcoming,
                                  this.colors.calibrated,
                              ]
                            : ["#e5e7eb"],
                        borderWidth: 0,
                        hoverOffset: 4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                cutout: "65%",
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: hasData,
                        callbacks: {
                            label: (context) => {
                                const label = context.label || "";
                                const value = context.parsed;
                                return ` ${label}: ${value}`;
                            },
                        },
                    },
                },
            },
        });
    }

    public trackById = trackById;
}
